/**
 * ניהול → מאזן — the day-to-day operations a full admin AND the מנהל אזור of מאזן
 * share: the new-report email alert, Mazan-only coupons, and confirming a Mazan customer's email (bypassing
 * the verification link, like the admin "אימות דוא״ל" override).
 *
 * Deliberately separate from the reports list, which stays full-admin only.
 */

import { useCallback, useEffect, useState } from 'react'
import { Bell, CheckCircle2, Plus, Power, Search, Trash2 } from 'lucide-react'
import api, { handleApiError } from '../../services/api'
import type { Coupon } from '../../types'

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString('he-IL') : '—')
const discount = (c: Coupon) => (c.discountType === 'percentage' ? `${c.discountValue}%` : `${c.discountValue} ₪`)

export function MazanCouponsCard({ onNotice }: { onNotice: (msg: string) => void }) {
  const [coupons, setCoupons] = useState<Coupon[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [form, setForm] = useState({ code: '', description: '', discountType: 'percentage' as 'percentage' | 'fixed', discountValue: '', validUntil: '', maxRedemptions: '' })

  const load = useCallback(() => {
    api.get<{ coupons: Coupon[] }>('/admin/mazan/coupons').then((r) => setCoupons(r.data.coupons)).catch((e) => setError(handleApiError(e)))
  }, [])
  useEffect(() => { load() }, [load])

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy('create')
    setError(null)
    try {
      await api.post('/admin/mazan/coupons', {
        code: form.code.trim(),
        description: form.description.trim() || undefined,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        validUntil: form.validUntil || undefined,
        maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : undefined,
      })
      setForm({ code: '', description: '', discountType: 'percentage', discountValue: '', validUntil: '', maxRedemptions: '' })
      onNotice('הקופון נוצר')
      load()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setBusy(null)
    }
  }

  const toggle = async (c: Coupon) => {
    setBusy(c._id)
    try {
      await api.post(`/admin/mazan/coupons/${c._id}/toggle-active`, { active: !c.active })
      onNotice(c.active ? `הקופון ${c.code} הושבת` : `הקופון ${c.code} הופעל`)
      load()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setBusy(null)
    }
  }

  const remove = async (c: Coupon) => {
    if (!window.confirm(`למחוק את הקופון ${c.code}?`)) return
    setBusy(c._id)
    try {
      await api.delete(`/admin/mazan/coupons/${c._id}`)
      onNotice(`הקופון ${c.code} נמחק`)
      load()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4" aria-labelledby="mz-coupons-title">
      <h2 id="mz-coupons-title" className="text-lg font-semibold text-gray-900">קופונים לדוח איזון</h2>
      <p className="text-sm text-gray-700">קופונים כאן חלים על דוח איזון בלבד — לא על מנויי הייעוץ.</p>
      <p className="text-sm text-gray-700">קופון של 100% (לבדיקות) מפיק את הדוח ללא תשלום — הלקוח לא יתבקש להזין כרטיס אשראי או Bit.</p>
      {error && <p role="alert" className="mt-2 rounded bg-red-50 p-2 text-sm text-red-800">{error}</p>}

      <form className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-6" onSubmit={create}>
        <div>
          <label htmlFor="mzc-code" className="mb-1 block text-xs font-medium text-gray-700">קוד</label>
          <input id="mzc-code" className="input" dir="ltr" required minLength={2} maxLength={40} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
        </div>
        <div>
          <label htmlFor="mzc-type" className="mb-1 block text-xs font-medium text-gray-700">סוג הנחה</label>
          <select id="mzc-type" className="input" value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value as 'percentage' | 'fixed' })}>
            <option value="percentage">אחוזים</option>
            <option value="fixed">סכום קבוע (₪)</option>
          </select>
        </div>
        <div>
          <label htmlFor="mzc-value" className="mb-1 block text-xs font-medium text-gray-700">{form.discountType === 'percentage' ? 'אחוז הנחה' : 'סכום הנחה (₪)'}</label>
          <input id="mzc-value" className="input" type="number" required min={1} max={form.discountType === 'percentage' ? 100 : 100000} value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} />
        </div>
        <div>
          <label htmlFor="mzc-until" className="mb-1 block text-xs font-medium text-gray-700">בתוקף עד (לא חובה)</label>
          <input id="mzc-until" className="input" type="date" value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
        </div>
        <div>
          <label htmlFor="mzc-max" className="mb-1 block text-xs font-medium text-gray-700">מגבלת שימושים (לא חובה)</label>
          <input id="mzc-max" className="input" type="number" min={1} value={form.maxRedemptions} onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })} />
        </div>
        <div className="flex items-end">
          <button type="submit" className="btn btn-primary inline-flex w-full items-center justify-center gap-1" disabled={busy === 'create'}>
            <Plus size={16} aria-hidden="true" /> יצירת קופון
          </button>
        </div>
        <div className="sm:col-span-3 lg:col-span-6">
          <label htmlFor="mzc-desc" className="mb-1 block text-xs font-medium text-gray-700">תיאור (לא חובה)</label>
          <input id="mzc-desc" className="input" maxLength={200} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </form>

      {coupons === null ? (
        <p role="status" className="mt-4 text-gray-700">טוען…</p>
      ) : coupons.length === 0 ? (
        <p className="mt-4 text-gray-700">אין עדיין קופונים לדוח איזון.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <caption className="sr-only">קופונים לדוח איזון</caption>
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th scope="col" className="px-3 py-2 text-start">קוד</th>
                <th scope="col" className="px-3 py-2 text-start">הנחה</th>
                <th scope="col" className="px-3 py-2 text-start">שימושים</th>
                <th scope="col" className="px-3 py-2 text-start">בתוקף עד</th>
                <th scope="col" className="px-3 py-2 text-start">סטטוס</th>
                <th scope="col" className="px-3 py-2 text-start"><span className="sr-only">פעולות</span></th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c._id} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium" dir="ltr">{c.code}</td>
                  <td className="px-3 py-2">{discount(c)}</td>
                  <td className="px-3 py-2">{c.redemptionCount}{c.maxRedemptions ? ` / ${c.maxRedemptions}` : ''}</td>
                  <td className="px-3 py-2">{fmtDate(c.validUntil)}</td>
                  <td className="px-3 py-2">{c.active ? 'פעיל' : 'מושבת'}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-3">
                      <button type="button" className="inline-flex items-center gap-1 text-primary-700 underline" disabled={busy === c._id} onClick={() => toggle(c)}>
                        <Power size={14} aria-hidden="true" /> {c.active ? 'השבתה' : 'הפעלה'}<span className="sr-only"> של {c.code}</span>
                      </button>
                      <button type="button" className="inline-flex items-center gap-1 text-red-800 underline" disabled={busy === c._id} onClick={() => remove(c)}>
                        <Trash2 size={14} aria-hidden="true" /> מחיקה<span className="sr-only"> של {c.code}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

interface MazanUserRow { id: string; name: string; email: string; emailVerified: boolean; status: string; createdAt: string }

export function MazanUsersCard({ onNotice }: { onNotice: (msg: string) => void }) {
  const [users, setUsers] = useState<MazanUserRow[] | null>(null)
  const [pendingOnly, setPendingOnly] = useState(true)
  const [q, setQ] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(() => {
    setError(null)
    api
      .get<{ users: MazanUserRow[] }>('/admin/mazan/users', { params: { pending: pendingOnly ? '1' : undefined, q: q || undefined } })
      .then((r) => setUsers(r.data.users))
      .catch((e) => setError(handleApiError(e)))
  }, [pendingOnly, q])
  useEffect(() => { load() }, [load])

  const verify = async (u: MazanUserRow) => {
    if (!window.confirm(`לאשר את הדוא״ל של ${u.name || u.email} ולהפעיל את החשבון?`)) return
    setBusy(u.id)
    try {
      await api.post(`/admin/mazan/users/${u.id}/verify-email`)
      onNotice(`החשבון של ${u.name || u.email} אושר`)
      load()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4" aria-labelledby="mz-users-title">
      <h2 id="mz-users-title" className="text-lg font-semibold text-gray-900">לקוחות איזון — אישור משתמשים</h2>
      <p className="text-sm text-gray-700">לקוח שלא קיבל את מייל האימות — אפשר לאשר את החשבון ידנית כאן.</p>
      <form className="mt-3 flex flex-wrap items-end gap-3" role="search" onSubmit={(e) => { e.preventDefault(); load() }}>
        <div>
          <label htmlFor="mzu-q" className="mb-1 block text-xs font-medium text-gray-700">חיפוש (שם / מייל)</label>
          <input id="mzu-q" className="input" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex items-center gap-2 pb-2">
          <input id="mzu-pending" type="checkbox" className="h-4 w-4" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
          <label htmlFor="mzu-pending" className="text-sm text-gray-800">ממתינים לאימות בלבד</label>
        </div>
        <button type="submit" className="btn btn-secondary inline-flex items-center gap-1"><Search size={16} aria-hidden="true" /> חיפוש</button>
      </form>
      {error && <p role="alert" className="mt-2 rounded bg-red-50 p-2 text-sm text-red-800">{error}</p>}
      {users === null ? (
        <p role="status" className="mt-4 text-gray-700">טוען…</p>
      ) : users.length === 0 ? (
        <p className="mt-4 text-gray-700">{pendingOnly ? 'אין לקוחות שממתינים לאימות.' : 'אין לקוחות להצגה.'}</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <caption className="sr-only">לקוחות איזון</caption>
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th scope="col" className="px-3 py-2 text-start">לקוח</th>
                <th scope="col" className="px-3 py-2 text-start">נרשם</th>
                <th scope="col" className="px-3 py-2 text-start">דוא״ל מאומת</th>
                <th scope="col" className="px-3 py-2 text-start"><span className="sr-only">פעולות</span></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    <span className="font-medium text-gray-900">{u.name || '—'}</span>
                    <span className="block text-xs text-gray-600" dir="ltr">{u.email}</span>
                  </td>
                  <td className="px-3 py-2">{fmtDate(u.createdAt)}</td>
                  <td className="px-3 py-2">{u.emailVerified ? 'כן' : 'לא'}</td>
                  <td className="px-3 py-2">
                    {!u.emailVerified && (
                      <button type="button" className="btn btn-primary inline-flex items-center gap-1" disabled={busy === u.id} onClick={() => verify(u)}>
                        <CheckCircle2 size={16} aria-hidden="true" /> אישור משתמש<span className="sr-only"> {u.name || u.email}</span>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

interface NotifySettings { enabled: boolean; emails: string[]; configured: boolean }

/**
 * "A new report was produced" summary email to the office — on/off + up to two
 * addresses. Until it is saved once, the server falls back to the env address.
 */
export function MazanNotifyCard({ onNotice }: { onNotice: (msg: string) => void }) {
  const [loaded, setLoaded] = useState<NotifySettings | null>(null)
  const [enabled, setEnabled] = useState(false)
  const [emails, setEmails] = useState<[string, string]>(['', ''])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const apply = (n: NotifySettings) => {
    setLoaded(n)
    setEnabled(n.enabled)
    setEmails([n.emails[0] || '', n.emails[1] || ''])
  }
  useEffect(() => {
    api.get<{ notify: NotifySettings }>('/admin/mazan/settings').then((r) => apply(r.data.notify)).catch((e) => setError(handleApiError(e)))
  }, [])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const list = emails.map((x) => x.trim()).filter(Boolean)
    if (enabled && !list.length) { setError('יש להזין לפחות כתובת דוא״ל אחת, או לכבות את ההתראות'); document.getElementById('mz-notify-1')?.focus(); return }
    setSaving(true)
    try {
      const r = await api.put<{ notify: NotifySettings }>('/admin/mazan/settings', { notify: { enabled, emails: list } })
      apply(r.data.notify)
      onNotice(enabled ? 'התראות על דוחות חדשים נשמרו' : 'התראות על דוחות חדשים כובו')
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setSaving(false)
    }
  }

  if (!loaded && !error) return <p role="status" className="text-gray-700">טוען הגדרות התראה…</p>

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4" aria-labelledby="mz-notify-title">
      <h2 id="mz-notify-title" className="flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Bell size={18} aria-hidden="true" /> התראה במייל על דוח חדש
      </h2>
      <p className="mt-1 text-sm text-gray-700">
        כשלקוח משלם ודוח איזון מופק, יישלח מייל עם שמות הצדדים, סיכום הסכומים (נטו, פנסיה, נכסים הוניים), הסכום ששולם
        וקישור לממשק הניהול לפרטים המלאים ולהורדת הדוח. קובץ הדוח עצמו אינו מצורף למייל.
      </p>
      {loaded && !loaded.configured && (
        <p className="mt-2 rounded bg-amber-50 p-2 text-sm text-amber-950">
          טרם נשמרו הגדרות — כרגע נשלחת התראה לכתובת ברירת המחדל של המערכת{loaded.emails.length ? ` (${loaded.emails.join(', ')})` : ''}. שמירה כאן תחליף אותה.
        </p>
      )}
      {error && <p role="alert" className="mt-2 rounded bg-red-50 p-2 text-sm text-red-800">{error}</p>}
      <form className="mt-3 space-y-3" onSubmit={save} noValidate>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-900">
          <input type="checkbox" className="h-4 w-4" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          לשלוח התראה על כל דוח חדש
        </label>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {([0, 1] as const).map((k) => (
            <div key={k}>
              <label htmlFor={`mz-notify-${k + 1}`} className="mb-1 block text-sm font-medium text-gray-800">
                {k === 0 ? 'כתובת דוא״ל ראשונה' : 'כתובת דוא״ל שנייה (לא חובה)'}
              </label>
              <input
                id={`mz-notify-${k + 1}`}
                type="email"
                dir="ltr"
                autoComplete="off"
                className="input"
                disabled={!enabled}
                value={emails[k]}
                onChange={(e) => setEmails((cur) => (k === 0 ? [e.target.value, cur[1]] : [cur[0], e.target.value]))}
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-600">הקישור במייל פותח את הדוח בממשק הניהול; צפייה בדוח המלא והורדתו פתוחות למנהלי מערכת בלבד.</p>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'שומר…' : 'שמירת הגדרות התראה'}</button>
      </form>
    </section>
  )
}
